import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, AutoComplete, Button, Checkbox, Col, ConfigProvider, Descriptions, Divider, Form, Input, Modal, Row, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import container from '@/lib/di/container';
import { MODAL_THEME } from '@/core/themes/modal.theme';
import AuthRepo from '@/core/repos/auth.repo';
import { ContractRepo } from '@/core/repos/tables/contract.repo';
import { ElectronicContractRepo } from '@/core/repos/tables/electronic-contract.repo';
import {
    CreateElectronicContractModel, ElectronicContractCustomerType, ElectronicContractModel,
    ElectronicContractPreview, ElectronicContractTemplateDefinition, ElectronicContractTemplateModel,
} from '@/core/models/electronic-contract.model';
import { ApiResponse } from '@/core/models/response.model';
import { createContractRequestKey, documentTypeLabels, getTemplateVariant } from '@/core/utils/electronic-contract';
import { ContractFields, contractFieldLabels } from './ContractFields';
import { buildCreationPayload, canIssuePreview, clearRequestMarker, getDraftStorageKey, readRequestMarker, RequestMarker, writeRequestMarker } from './creation-draft';

const contractRepo = container.get(ContractRepo);
const electronicContractRepo = container.get(ElectronicContractRepo);
const authRepo = container.get(AuthRepo);

interface CreateElectronicContractMultiModalProps {
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    query?: any;
}
interface PreviewSnapshot {
    response: ApiResponse<ElectronicContractPreview>;
    payload: CreateElectronicContractModel;
}

const unresolvedStatuses = ['Creating', 'CreateUncertain', 'Failed'];

/** The historical export is retained so existing pages open this single-customer V2 workflow. */
export const CreateElectronicContractMultiModal: React.FC<CreateElectronicContractMultiModalProps> = ({ isModalOpen, setIsModalOpen }) => {
    const details = authRepo.useDetails();
    const scope = details?.id && details.waterFactoryId
        ? getDraftStorageKey(process.env.REACT_APP_API_URL ?? window.location.origin, details.waterFactoryId, details.id) : '';
    const [marker, setMarker] = useState<RequestMarker | null>(null);
    const markerRef = useRef<RequestMarker | null>(null);
    const [customerType, setCustomerType] = useState<ElectronicContractCustomerType>();
    const [documentType, setDocumentType] = useState<string>();
    const [templateId, setTemplateId] = useState<string>();
    const [waterUserCode, setWaterUserCode] = useState('');
    const [values, setValues] = useState<Record<string, string>>({});
    const [catalog, setCatalog] = useState<ElectronicContractTemplateDefinition[]>([]);
    const [templates, setTemplates] = useState<ElectronicContractTemplateModel[]>([]);
    const [customerOptions, setCustomerOptions] = useState<{ value: string; label: string }[]>([]);
    const [preview, setPreview] = useState<PreviewSnapshot | null>(null);
    const [confirmed, setConfirmed] = useState(false);
    const [success, setSuccess] = useState<ElectronicContractModel | null>(null);
    const [error, setError] = useState('');
    const [configurationError, setConfigurationError] = useState('');
    const [storageError, setStorageError] = useState(false);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [loadingCatalog, setLoadingCatalog] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [creating, setCreating] = useState(false);
    const [reconciling, setReconciling] = useState(false);
    const revisionRef = useRef(0);
    const activeScopeRef = useRef(scope);
    activeScopeRef.current = scope;
    const locked = creating || !!success || (!!marker && marker.state !== 'draft');
    const definition = catalog.find(item => item.documentType === documentType && item.customerType === customerType);
    const compatibleTemplates = useMemo(() => templates.filter(template => {
        const variant = getTemplateVariant(template);
        return template.status === 'Active' && variant?.customerType === customerType && variant?.documentType === documentType;
    }), [templates, customerType, documentType]);

    const invalidatePreview = () => {
        revisionRef.current += 1;
        setPreview(null);
        setConfirmed(false);
        setError('');
    };
    const updateMarker = (next: RequestMarker, storageScope = scope) => {
        markerRef.current = next;
        setMarker(next);
        const stored = !!storageScope && writeRequestMarker(storageScope, next);
        setStorageError(!stored);
        return stored;
    };
    const resetFields = () => {
        invalidatePreview();
        setCustomerType(undefined);
        setDocumentType(undefined);
        setTemplateId(undefined);
        setWaterUserCode('');
        setValues({});
        setCatalog([]);
        setCustomerOptions([]);
        setSuccess(null);
    };

    useEffect(() => {
        if (!isModalOpen || !scope) return;
        resetFields();
        setCreating(false);
        setReconciling(false);
        const saved = readRequestMarker(scope);
        try {
            updateMarker(saved ?? { requestKey: createContractRequestKey(), state: 'draft' }, scope);
        } catch {
            markerRef.current = null;
            setMarker(null);
            setStorageError(true);
            setError('Không thể tạo khóa yêu cầu an toàn. Vui lòng dùng trình duyệt có hỗ trợ mã hóa.');
        }
        setConfigurationError('');
        let current = true;
        setLoadingTemplates(true);
        electronicContractRepo.getTemplates().then(response => {
            if (!current) return;
            if (response?.code === '00' && Array.isArray(response.result)) setTemplates(response.result);
            else { setTemplates([]); setConfigurationError(response?.errorMessage || 'Không tải được danh sách mẫu đang hoạt động.'); }
        }).catch(() => { if (current) setConfigurationError('Không tải được danh sách mẫu đang hoạt động.'); })
            .finally(() => { if (current) setLoadingTemplates(false); });
        return () => { current = false; revisionRef.current += 1; };
        // Reopening restores only the request marker; customer data is never stored in the browser.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen, scope]);

    useEffect(() => {
        if (!isModalOpen || !customerType) return;
        let current = true;
        setLoadingCatalog(true);
        setConfigurationError('');
        electronicContractRepo.getTemplateCatalog(customerType).then(response => {
            if (!current) return;
            if (response?.code === '00' && Array.isArray(response.result)) setCatalog(response.result);
            else { setCatalog([]); setConfigurationError(response?.errorMessage || 'Không tải được danh mục trường của mẫu.'); }
        }).catch(() => { if (current) setConfigurationError('Không tải được danh mục trường của mẫu.'); })
            .finally(() => { if (current) setLoadingCatalog(false); });
        return () => { current = false; };
    }, [isModalOpen, customerType]);

    useEffect(() => {
        if (!isModalOpen || locked || !waterUserCode.trim()) { setCustomerOptions([]); return; }
        let current = true;
        const timer = setTimeout(() => {
            contractRepo.getData(1, { search: waterUserCode }, 10).then(page => {
                if (current) setCustomerOptions((page?.data ?? []).map(customer => ({ value: String(customer.code), label: `${customer.code} · ${customer.name}` })));
            }).catch(() => { if (current) setCustomerOptions([]); });
        }, 300);
        return () => { current = false; clearTimeout(timer); };
    }, [isModalOpen, waterUserCode, locked]);

    const handlePreview = async () => {
        if (locked || loadingPreview || !definition || !templateId || !waterUserCode.trim() || !marker || !compatibleTemplates.some(template => template.id === templateId)) return;
        invalidatePreview();
        const revision = revisionRef.current;
        const payload = buildCreationPayload(waterUserCode, templateId, marker.requestKey, definition, values);
        setLoadingPreview(true);
        try {
            const response = await electronicContractRepo.previewFields(payload);
            if (revision !== revisionRef.current) return;
            if (response?.result) setPreview({ response, payload });
            if (response?.code !== '00') setError(response?.errorMessage || 'Không thể kiểm tra dữ liệu. Vui lòng thử xem trước lại.');
        } catch { if (revision === revisionRef.current) setError('Không thể kiểm tra dữ liệu. Vui lòng thử xem trước lại.'); }
        finally { setLoadingPreview(false); }
    };

    const handleCreate = async () => {
        if (creating || markerRef.current?.state !== 'draft' || !preview || !confirmed || !canIssuePreview(preview.response) || !scope || !marker) return;
        const submitting: RequestMarker = { requestKey: marker.requestKey, state: 'submitting' };
        // Persist before the network call, so a reload cannot silently open another issuance.
        if (!writeRequestMarker(scope, submitting)) {
            setStorageError(true);
            setError('Không lưu được khóa phát hành. Kiểm tra quyền lưu trữ của trình duyệt trước khi tạo.');
            return;
        }
        markerRef.current = submitting;
        setMarker(submitting);
        setStorageError(false);
        const submittedScope = scope;
        setCreating(true);
        setError('');
        try {
            const response = await electronicContractRepo.createContract(preview.payload);
            if (activeScopeRef.current !== submittedScope) return;
            const result = response?.result;
            if (response?.code === '00' && result?.id && result.documentId && !unresolvedStatuses.includes(result.status ?? '')) {
                clearRequestMarker(submittedScope);
                setSuccess(result);
                electronicContractRepo.refresh();
            } else if (response?.code === '01' && !result?.id) {
                // Backend validation failures without a record occur before provider submission.
                // Keep the same key while staff corrects the form and previews again.
                updateMarker({ requestKey: submitting.requestKey, state: 'draft' }, submittedScope);
                setPreview(null);
                setConfirmed(false);
                setError(response.errorMessage || 'Dữ liệu chưa hợp lệ. Kiểm tra và xem trước lại trước khi phát hành.');
            } else {
                updateMarker({ requestKey: submitting.requestKey, state: result?.status === 'Failed' ? 'failed' : 'uncertain' }, submittedScope);
                setError(response?.errorMessage || 'Chưa xác định kết quả phát hành. Tra cứu hồ sơ theo khóa yêu cầu và đối soát trước thao tác tiếp theo.');
            }
        } catch {
            const uncertain: RequestMarker = { requestKey: submitting.requestKey, state: 'uncertain' };
            if (activeScopeRef.current === submittedScope) {
                updateMarker(uncertain, submittedScope);
                setError('Mất kết nối khi tạo hồ sơ. Kết quả chưa rõ; cần đối soát trước khi tiếp tục.');
            } else {
                writeRequestMarker(submittedScope, uncertain);
            }
        } finally { if (activeScopeRef.current === submittedScope) setCreating(false); }
    };

    const handleReconcile = async () => {
        if (!marker || reconciling || creating) return;
        const reconciledScope = scope;
        setReconciling(true);
        setError('');
        try {
            // The backend uses the normalized request GUID as the WaterMS record ID.
            const record = await electronicContractRepo.getById(marker.requestKey);
            if (activeScopeRef.current !== reconciledScope) return;
            if (record?.id && record.documentId && !unresolvedStatuses.includes(record.status ?? '')) {
                clearRequestMarker(scope);
                setSuccess(record);
                electronicContractRepo.refresh();
            } else {
                setError(record?.errorMessage || 'Chưa có kết quả phát hành chắc chắn. Mở danh sách để đồng bộ hồ sơ hoặc liên hệ nhân viên vận hành với khóa yêu cầu bên dưới.');
            }
        } catch {
            if (activeScopeRef.current === reconciledScope) setError('Không tra cứu được kết quả. Giữ nguyên khóa yêu cầu và đối soát với nhân viên vận hành.');
        } finally { if (activeScopeRef.current === reconciledScope) setReconciling(false); }
    };
    const startNew = () => {
        if (!success) return;
        try {
            const requestKey = createContractRequestKey();
            resetFields();
            updateMarker({ requestKey, state: 'draft' });
        } catch { setError('Không thể tạo khóa yêu cầu an toàn. Vui lòng dùng trình duyệt có hỗ trợ mã hóa.'); }
    };
    const previewData = preview?.response.result;
    const showPending = marker && marker.state !== 'draft' && !success;

    return <ConfigProvider theme={MODAL_THEME}>
        <Modal open={isModalOpen} title="TẠO HỒ SƠ ĐIỆN TỬ" width={1100} centered maskClosable={false}
            onCancel={() => setIsModalOpen(false)} styles={{ mask: { backdropFilter: 'blur(2px)', backgroundColor: 'var(--primary-mask)' } }}
            footer={<Space wrap>
                <Button onClick={() => setIsModalOpen(false)}>{showPending || success ? 'Về danh sách' : 'Đóng'}</Button>
                {success ? <Button type="primary" onClick={startNew}>Tạo hồ sơ mới</Button> : showPending ?
                    <Button loading={reconciling} disabled={creating} onClick={handleReconcile}>Kiểm tra kết quả phát hành</Button> : <>
                        <Button loading={loadingPreview} disabled={locked || !scope || !marker || !definition || !templateId || !waterUserCode.trim() || loadingCatalog || loadingTemplates} onClick={handlePreview}>Xem trước dữ liệu</Button>
                        <Button type="primary" loading={creating} disabled={locked || storageError || !confirmed || !preview || !canIssuePreview(preview.response)} onClick={handleCreate}>Phát hành hồ sơ</Button>
                    </>}
            </Space>}>
            {!scope && <Alert type="warning" showIcon message="Đang chờ thông tin tài khoản và nhà máy để mở bản nháp an toàn." />}
            {configurationError && <Alert className="mb-4" type="error" showIcon message={configurationError} />}
            {storageError && <Alert className="mb-4" type="error" showIcon message="Không lưu được khóa yêu cầu trong trình duyệt. Chức năng phát hành đang khóa." />}
            {success && <Alert className="mb-4" type="success" showIcon message="Đã tạo hồ sơ; tiếp tục kiểm tra người ký và trạng thái trên danh sách."
                description={<Space direction="vertical"><span>ID WaterMS: {success.id}</span><span>Mã chứng từ: {success.documentCode || success.documentId}</span><span>Khóa yêu cầu: {marker?.requestKey}</span></Space>} />}
            {showPending && <Alert className="mb-4" type="warning" showIcon message={creating ? 'Đang gửi yêu cầu phát hành' : 'Hồ sơ cần đối soát — đã khóa tạo lại'}
                description={<><p>Giữ nguyên khóa yêu cầu. Về danh sách để đồng bộ hồ sơ hoặc liên hệ nhân viên vận hành; không gửi phát hành lại khi kết quả chưa rõ.</p><Typography.Text copyable>{marker.requestKey}</Typography.Text></>} />}
            {error && <Alert className="mb-4" type="error" showIcon message={error} />}
            <Form layout="vertical" className="mt-4">
                <Row gutter={20}>
                    <Col xs={24} md={12}><Form.Item label="Loại khách hàng" required>
                        <Select aria-label="Loại khách hàng" placeholder="Chọn rõ cá nhân hoặc doanh nghiệp" value={customerType} disabled={locked || !scope}
                            options={[{ value: 'CN', label: 'CN — Cá nhân' }, { value: 'DN', label: 'DN — Doanh nghiệp / tổ chức' }]}
                            onChange={value => { invalidatePreview(); setCustomerType(value); setDocumentType(undefined); setTemplateId(undefined); setCatalog([]); setValues({}); }} />
                    </Form.Item></Col>
                    <Col xs={24} md={12}><Form.Item label="Loại chứng từ" required>
                        <Select aria-label="Loại chứng từ" placeholder="Chọn loại chứng từ" loading={loadingCatalog} value={documentType} disabled={locked || !customerType || loadingCatalog}
                            options={catalog.map(item => ({ value: item.documentType, label: documentTypeLabels[item.documentType] ?? item.documentType }))}
                            onChange={value => { invalidatePreview(); setDocumentType(value); setTemplateId(undefined); setValues({}); }} />
                    </Form.Item></Col>
                    <Col xs={24} md={12}><Form.Item label="Mẫu đang hoạt động của nhà máy" required>
                        <Select aria-label="Mẫu đang hoạt động của nhà máy" placeholder="Chọn đúng mẫu CN/DN" value={templateId} loading={loadingTemplates} disabled={locked || !documentType || loadingTemplates}
                            options={compatibleTemplates.map(template => ({ value: template.id, label: template.name || template.id }))}
                            notFoundContent="Không có mẫu Active khớp loại khách hàng và chứng từ"
                            onChange={value => { invalidatePreview(); setTemplateId(value); setValues({}); }} />
                    </Form.Item></Col>
                    <Col xs={24} md={12}><Form.Item label="Khách hàng" required extra="Nhập nguyên mã khách hàng hoặc tìm rồi chọn khách hàng. Giữ nguyên số 0 đầu.">
                        <AutoComplete className="w-full" value={waterUserCode} options={customerOptions} filterOption={false} disabled={locked || !scope}
                            onChange={value => { invalidatePreview(); setWaterUserCode(value); setValues({}); }}>
                            <Input aria-label="Mã khách hàng" autoComplete="off" placeholder="Mã khách hàng / tìm theo tên" />
                        </AutoComplete>
                    </Form.Item></Col>
                </Row>
                <Spin spinning={loadingCatalog}>
                    {definition && templateId && <>
                        <Divider orientation="left">Thông tin bổ sung của chứng từ</Divider>
                        {!definition.canCreateFromTemplate && <Alert className="mb-4" type="warning" showIcon message="Mẫu này có giới hạn phát hành. Kết quả xem trước sẽ quyết định có được tạo trong môi trường hiện tại." />}
                        <ContractFields definition={definition} values={values} disabled={locked} onChange={(name, value) => {
                            invalidatePreview(); setValues(previous => ({ ...previous, [name]: value }));
                        }} />
                    </>}
                </Spin>
            </Form>
            {previewData && <>
                <Divider orientation="left">Kiểm tra dữ liệu sẽ điền</Divider>
                <Space wrap className="mb-4"><Tag>{previewData.customerType}</Tag><Tag>{previewData.generationMode}</Tag></Space>
                <Alert className="mb-4" type="info" showIcon message="Đây là dữ liệu điền mẫu, chưa phải PDF cuối cùng của nhà cung cấp." />
                {!previewData.canCreateFromTemplate && <Alert className="mb-4" type="warning" showIcon message="Mẫu chưa được phép phát hành trong môi trường hiện tại. Mẫu 02 cần cấu hình demo hoặc quy trình PDF đã duyệt." />}
                {!!previewData.missingRequiredFields?.length && <Alert className="mb-4" type="error" showIcon message="Cần bổ sung đầy đủ các mục sau"
                    description={<ul className="list-disc pl-5">{previewData.missingRequiredFields.map((field, index) => <li key={`${field}-${index}`}>{contractFieldLabels[field] ? `${contractFieldLabels[field]} (${field})` : field}</li>)}</ul>} />}
                <Typography.Title level={5}>Bên ký và thông tin liên hệ</Typography.Title>
                <Descriptions bordered size="small" column={{ xs: 1, sm: 1, md: 2 }} items={Object.entries(previewData.customerIdentity ?? {}).map(([name, value]) => ({ key: name, label: contractFieldLabels[name] ?? name, children: String(value ?? '') }))} />
                <Typography.Title level={5}>Các trường trên chứng từ</Typography.Title>
                <Table size="small" pagination={false} scroll={{ y: 300 }} rowKey="name"
                    dataSource={Object.entries(previewData.fields ?? {}).map(([name, value]) => ({ name, value }))}
                    columns={[{ title: 'Trường', dataIndex: 'name', render: (name: string) => contractFieldLabels[name] ? `${contractFieldLabels[name]} (${name})` : name }, { title: 'Giá trị', dataIndex: 'value' }]} />
                <div className="my-4"><Checkbox checked={confirmed} disabled={locked || !canIssuePreview(preview.response)} onChange={event => setConfirmed(event.target.checked)}>
                    Tôi đã kiểm tra bên ký, căn cứ đại diện, email nhận OTP, mã hợp đồng, ngày, chỉ số và chi phí trong dữ liệu xem trước.
                </Checkbox></div>
            </>}
        </Modal>
    </ConfigProvider>;
};
